import React, { useEffect, useState } from "react";
import { Users, X } from "lucide-react";
import { apiFetch } from "../utils/apiClient";
import { showToast } from "../utils/toast";

const notifyStewardMode = (settings) => {
  try {
    localStorage.setItem("moo_steward_role_enabled", String(settings.enabled));
    localStorage.setItem("moo_steward_mode_broadcast", String(Date.now()));
  } catch (_error) {}
  window.dispatchEvent(new CustomEvent("moo-steward-mode-updated", {
    detail: settings,
  }));
};

const StewardRoleSettings = ({ cardClasses = "", isDarkMode = true }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    replaces_server: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showActivation, setShowActivation] = useState(false);
  const [activationChoice, setActivationChoice] = useState(true);
  const [error, setError] = useState("");

  const request = (url, options = {}) => apiFetch(url, {
    includeBearer: true,
    tokenMode: "superadmin",
    credentials: "include",
    cache: "no-store",
    ...options,
  });

  useEffect(() => {
    let active = true;
    request(`/api/stewards/settings?_ts=${Date.now()}`)
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "Chargement impossible.");
        if (active) {
          const next = {
            enabled: Boolean(payload.enabled),
            replaces_server: payload.replaces_server !== false,
          };
          setSettings(next);
          notifyStewardMode(next);
        }
      })
      .catch((loadError) => active && setError(loadError.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const save = async (next) => {
    setSaving(true);
    setError("");
    try {
      const response = await request("/api/stewards/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Enregistrement impossible.");
      const saved = {
        enabled: Boolean(payload.enabled),
        replaces_server: payload.replaces_server !== false,
      };
      setSettings(saved);
      notifyStewardMode(saved);
      showToast("success", saved.enabled ? "Rôle Steward activé" : "Rôle Steward désactivé");
      return true;
    } catch (saveError) {
      setError(saveError.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    if (settings.enabled) {
      await save({ ...settings, enabled: false });
      return;
    }
    setActivationChoice(true);
    setShowActivation(true);
  };

  const confirmActivation = async () => {
    const saved = await save({
      enabled: true,
      replaces_server: activationChoice,
    });
    if (saved) setShowActivation(false);
  };

  const muted = isDarkMode ? "text-slate-300" : "text-slate-600";
  return (
    <div className="mx-auto max-w-3xl">
      <section className={`${cardClasses} rounded-3xl p-6`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="rounded-2xl bg-amber-400/15 p-3 text-amber-300">
              <Users size={22} />
            </span>
            <h2 className="text-2xl font-semibold">Rôle Steward</h2>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={settings.enabled}
            disabled={loading || saving}
            onClick={toggle}
            className={`rounded-2xl px-5 py-3 text-sm font-semibold ${
              settings.enabled ? "bg-emerald-500 text-white" : "bg-slate-500/30"
            } disabled:opacity-50`}
          >
            {settings.enabled ? "Activé" : "Désactivé"}
          </button>
        </div>

        {settings.enabled && (
          <div className="mt-6 border-t border-white/10 pt-5">
            <p className={`mb-3 text-sm ${muted}`}>Sur les reçus et rapports</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                [true, "Steward"],
                [false, "Utilisateur réel"],
              ].map(([value, label]) => (
                <button
                  key={label}
                  type="button"
                  disabled={saving}
                  onClick={() => save({ ...settings, replaces_server: value })}
                  className={`rounded-2xl border px-4 py-3 font-semibold ${
                    settings.replaces_server === value
                      ? "border-amber-300 bg-amber-400/15 text-amber-200"
                      : "border-white/10"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {error && <p className="mt-4 text-sm text-rose-400">{error}</p>}
      </section>

      {showActivation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0f172a] p-6 text-white shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-2xl font-semibold">Activer le rôle Steward</h3>
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => setShowActivation(false)}
                className="rounded-xl bg-white/10 p-2"
              >
                <X size={18} />
              </button>
            </div>
            <p className="mt-5 text-lg">
              Le steward remplace-t-il le serveur sur les reçus, factures et rapports ?
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[[true, "Oui"], [false, "Non"]].map(([value, label]) => (
                <button
                  key={label}
                  type="button"
                  aria-pressed={activationChoice === value}
                  onClick={() => setActivationChoice(value)}
                  className={`rounded-2xl border px-5 py-4 text-lg font-semibold ${
                    activationChoice === value
                      ? "border-amber-300 bg-amber-400/15 text-amber-200"
                      : "border-white/10"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={confirmActivation}
              className="mt-6 w-full rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-4 font-bold text-slate-950 disabled:opacity-50"
            >
              Activer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StewardRoleSettings;
